import { COLORS } from "@coeff/utils";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import {
  dismissZendeskChatLoading,
  getZendeskChatLoadingSnapshot,
  subscribeZendeskChatLoading,
} from "../scripts";

const CHAT_OVERLAY_FADE_MS = 200;
const SHOW_CANCEL_AFTER_MS = 12_000;

const CSSSpinner = styled.div`
  display: inline-block;
  border: 3px solid ${COLORS.black15};
  border-top-color: ${COLORS.coeblue4};
  border-radius: 50%;
  animation: css-spin 0.8s linear infinite;
  will-change: transform;

  width: 24px;
  height: 24px;

  @keyframes css-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ChatLoadingOverlay = styled.div<{ $fadingOut?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: white;
  z-index: 300;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.black85};
  animation: ${({ $fadingOut }) => ($fadingOut ? "chat-overlay-fade-out" : "chat-overlay-fade-in")}
    ${CHAT_OVERLAY_FADE_MS}ms ease forwards;

  @keyframes chat-overlay-fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 0.85;
    }
  }

  @keyframes chat-overlay-fade-out {
    from {
      opacity: 0.85;
    }
    to {
      opacity: 0;
    }
  }
`;

const CancelButton = styled.button<{ $visible: boolean }>`
  margin-top: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.black85};
  background-color: ${COLORS.black15};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${CHAT_OVERLAY_FADE_MS}ms ease;
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

export const ZendeskChatLoadingOverlay: React.FC = () => {
  const [isChatLoading, setIsChatLoading] = useState(getZendeskChatLoadingSnapshot);
  const [visible, setVisible] = useState(isChatLoading);
  const [showCancel, setShowCancel] = useState(false);

  useEffect(
    () => subscribeZendeskChatLoading(() => setIsChatLoading(getZendeskChatLoadingSnapshot())),
    []
  );

  useEffect(() => {
    if (!isChatLoading) {
      setShowCancel(false);
      return;
    }
    const timerId = setTimeout(() => setShowCancel(true), SHOW_CANCEL_AFTER_MS);
    return () => clearTimeout(timerId);
  }, [isChatLoading]);

  useEffect(() => {
    if (isChatLoading) {
      setVisible(true);
      return;
    }
    if (!visible) {
      return;
    }
    const timer = setTimeout(() => setVisible(false), CHAT_OVERLAY_FADE_MS);
    return () => clearTimeout(timer);
  }, [isChatLoading, visible]);

  if (!visible && !isChatLoading) {
    return null;
  }

  return (
    <ChatLoadingOverlay $fadingOut={!isChatLoading}>
      <CSSSpinner />
      <span>Opening chat...</span>
      <CancelButton
        type="button"
        $visible={showCancel}
        onClick={() => {
          setShowCancel(false);
          dismissZendeskChatLoading();
        }}
      >
        Cancel
      </CancelButton>
    </ChatLoadingOverlay>
  );
};
